/**
 * THE FIVE SITES and the shape of a hunt.
 *
 * A hunt is ten waves. Sites change every two waves, so the scenery turns over
 * often enough that nobody is looking at the same treeline for three minutes —
 * the arcade seat's rule that a player decides in eleven seconds whether to
 * play again applies just as hard to the fourth minute as the first.
 *
 *   waves 1–2   Brushy Creek     dawn, cottonwoods, easy
 *   waves 3–4   Cornfield Draw   midday, standing corn, wave 4 is a BONUS
 *   waves 5–6   Pine Ridge       dusk, deep depth band, small targets
 *   waves 7–8   Whitetail Hollow overcast, fast and weaving, wave 8 is a BONUS
 *   waves 9–10  Frost Hollow     snow at night; wave 10 is the LEGEND
 *
 * The palettes live here rather than in the renderer because the site is a
 * gameplay object — depth band, cover, and difficulty are all site properties —
 * and splitting its look from its behaviour is how the two drift apart.
 */

import type { DeerKind, DeerRecipe } from './deer.js';

export type WaveKind = 'hunt' | 'varmint' | 'legend';

export interface SitePalette {
  /** Sky, top to horizon. */
  skyTop: string;
  skyBottom: string;
  /** Sun/moon disc, or null for overcast. */
  disc: string | null;
  discSy: number;
  discSx: number;
  /** Three treeline bands, far to near. */
  ridge: [string, string, string];
  /** Ground, at the horizon and in the foreground. */
  groundFar: string;
  groundNear: string;
  /** Silhouette colour for the animals. */
  animal: string;
  animalLight: string;
  /** Foreground cover drawn over the deer's legs. */
  cover: string;
  /** Atmospheric haze laid over distance. */
  haze: string;
  /** Weather particles. */
  weather: 'none' | 'mist' | 'snow' | 'dust';
  /**
   * Star density, 0 … 1. A property of the hour, which is a property of the
   * site — the renderer has no business inferring "it is night here" from how
   * dark `skyTop` happens to be.
   */
  stars: number;
  /** Cloud cover, 0 … 1. 1 is the overcast hollow. */
  clouds: number;
}

export interface Site {
  id: string;
  name: string;
  /** Shown under the name on the site card. */
  blurb: string;
  palette: SitePalette;
  /** What kind of cover is drawn along the bottom of the stage. */
  cover: 'brush' | 'corn' | 'pine' | 'thicket' | 'drift';
  /**
   * Which ambient bed plays under this site's waves. Purely cosmetic — the
   * server ships the whole `Site` record's id and nothing else, so this costs
   * the wire nothing. It lives here rather than in the renderer for the same
   * reason the palette does: the hour of the day is a property of the place.
   */
  ambience: 'dawn' | 'day' | 'dusk' | 'wind' | 'night';
}

export const SITES: readonly Site[] = [
  {
    id: 'brushy',
    name: 'Brushy Creek',
    blurb: 'First light. The mist is still down in the bottoms.',
    cover: 'brush',
    ambience: 'dawn',
    palette: {
      skyTop: '#1d2f4a',
      skyBottom: '#e8a06a',
      disc: '#ffd9a0',
      discSx: 0.38,
      discSy: -0.24,
      ridge: ['#4a5570', '#2f3a4e', '#1c2331'],
      groundFar: '#3b4030',
      groundNear: '#20241a',
      animal: '#241b12',
      animalLight: '#4a3826',
      cover: '#141a12',
      haze: 'rgba(232,160,106,0.30)',
      weather: 'mist',
      stars: 0.35,
      clouds: 0.55,
    },
  },
  {
    id: 'cornfield',
    name: 'Cornfield Draw',
    blurb: 'Standing corn, and something moving through it.',
    cover: 'corn',
    ambience: 'day',
    palette: {
      skyTop: '#3f7fbf',
      skyBottom: '#bfd9e8',
      disc: '#fff4d0',
      discSx: -0.42,
      discSy: -0.52,
      ridge: ['#8a9a6a', '#6a7a4a', '#4a5a30'],
      groundFar: '#9a8a45',
      groundNear: '#6b5c26',
      animal: '#2a1d10',
      animalLight: '#5a4020',
      cover: '#3d3812',
      haze: 'rgba(200,220,240,0.18)',
      weather: 'dust',
      stars: 0,
      clouds: 0.45,
    },
  },
  {
    id: 'pineridge',
    name: 'Pine Ridge',
    blurb: 'Long shots off the ridge. Light going fast.',
    cover: 'pine',
    ambience: 'dusk',
    palette: {
      skyTop: '#241a3a',
      skyBottom: '#c4574a',
      disc: '#ffb060',
      discSx: -0.30,
      discSy: -0.20,
      ridge: ['#3a3a54', '#252a3c', '#141826'],
      groundFar: '#2c3128',
      groundNear: '#161a14',
      animal: '#1a1410',
      animalLight: '#3a2c1e',
      cover: '#0e1410',
      haze: 'rgba(196,87,74,0.26)',
      weather: 'none',
      stars: 0.5,
      clouds: 0.7,
    },
  },
  {
    id: 'hollow',
    name: 'Whitetail Hollow',
    blurb: 'Thick, grey, and they will not stand still.',
    cover: 'thicket',
    ambience: 'wind',
    palette: {
      skyTop: '#7e8a94',
      skyBottom: '#b9c2c8',
      disc: null,
      discSx: 0,
      discSy: 0,
      ridge: ['#6b7566', '#4b5548', '#2f382e'],
      groundFar: '#4a5040',
      groundNear: '#282d20',
      animal: '#221a14',
      animalLight: '#453424',
      cover: '#1a2016',
      haze: 'rgba(185,194,200,0.22)',
      weather: 'mist',
      stars: 0,
      clouds: 1,
    },
  },
  {
    id: 'frost',
    name: 'Frost Hollow',
    blurb: 'Snow, moonlight, and the big one is out here.',
    cover: 'drift',
    ambience: 'night',
    palette: {
      skyTop: '#0a1020',
      skyBottom: '#28405e',
      disc: '#e8f0ff',
      discSx: 0.44,
      discSy: -0.56,
      ridge: ['#3c4a64', '#28324a', '#161d2c'],
      groundFar: '#8fa2bc',
      groundNear: '#c8d6e6',
      animal: '#141018',
      animalLight: '#33283a',
      cover: '#dce8f4',
      haze: 'rgba(40,64,94,0.34)',
      weather: 'snow',
      stars: 1,
      clouds: 0.3,
    },
  },
];

export const WAVES_PER_HUNT = 10;

export interface WavePlan {
  /** 1-based. */
  index: number;
  kind: WaveKind;
  site: Site;
  /** How long the herd runs, ms. */
  durationMs: number;
  /** Shells at the start of the wave. */
  shells: number;
  /** The animals, for a `hunt` wave. */
  recipes: DeerRecipe[];
  /** For a `varmint` wave. */
  varmints: VarmintRecipe[];
}

export interface VarmintRecipe {
  kind: 'critter' | 'skunk';
  sx: number;
  sy: number;
  /** Seconds after the wave starts. */
  delay: number;
  /** How long it is up, seconds. */
  up: number;
}

/** Reload takes this long. Server-enforced; the client only animates it. */
export const RELOAD_MS = 850;

/** The scoring beat between waves. */
export const INTERMISSION_MS = 4200;

/** Shown before wave 1 so nobody is aiming at a title card. */
export const COUNTDOWN_MS = 3200;

export function siteForWave(index: number): Site {
  return SITES[Math.min(SITES.length - 1, Math.floor((index - 1) / 2))]!;
}

export function waveKind(index: number): WaveKind {
  if (index === WAVES_PER_HUNT) return 'legend';
  if (index === 4 || index === 8) return 'varmint';
  return 'hunt';
}

/**
 * How much extra herd each additional hunter is worth.
 *
 * THE FINDING THAT PUT THIS HERE. `npm run sim` with six bots produced a hunt
 * in which three players finished on ZERO. Not because they were bad — because
 * a wave sized for one player, shared by six, is a wave where the fastest gun
 * takes everything and everyone else watches. Contested-kill counts confirmed
 * it: bots 3–6 were beaten to the animal 35+ times each.
 *
 * Bots overstate it — six real people do not all aim at the same pixel in the
 * same millisecond — but the shape of the problem is real, and it is the exact
 * failure mode a shared herd invites. So the herd scales with the rail: one
 * more animal per additional hunter, a little under one-for-one so a full
 * saloon is still busier per person than a solo hunt.
 */
const HERD_PER_EXTRA_HUNTER = 1.0;

/**
 * Build the plan for a wave. Deterministic given the seed, so every client can
 * describe the wave it is about to play before the first deer moves — which is
 * what lets the "GET READY · 4 BUCKS · WATCH FOR DOES" card be truthful.
 *
 * `hunters` is how many people actually anted up.
 */
export function planWave(index: number, rng: import('./rng.js').Rng, hunters = 1): WavePlan {
  const extra = Math.round(Math.max(0, hunters - 1) * HERD_PER_EXTRA_HUNTER);
  const site = siteForWave(index);
  const kind = waveKind(index);
  // 0 at wave 1, 1 at the last wave. Every difficulty knob hangs off this.
  const tier = (index - 1) / (WAVES_PER_HUNT - 1);

  if (kind === 'varmint') {
    return {
      index,
      kind,
      site,
      durationMs: 15_000,
      shells: 12,
      recipes: [],
      varmints: planVarmints(index, rng, extra),
    };
  }

  if (kind === 'legend') {
    return {
      index,
      kind,
      site,
      durationMs: 20_000,
      // Four shells, and only four, because the point of the last wave is that
      // you cannot spray at him. A crowded rail gets a couple more, since the
      // escorts below are also worth shooting.
      shells: 4 + Math.min(4, extra),
      recipes: [
        {
          kind: 'buck',
          rack: 20,
          speed: 0.62,
          zMin: 0.44,
          zMax: 0.52,
          leap: 0.16,
          weave: 0.22,
          delay: 1.6,
        },
        // Two does travel with him. Shooting one ends your shot at the legend,
        // which is the whole tension of the last wave.
        { kind: 'doe', rack: 0, speed: 0.7, zMin: 0.3, zMax: 0.4, leap: 0, weave: 0.18, delay: 4.5 },
        { kind: 'doe', rack: 0, speed: 0.66, zMin: 0.55, zMax: 0.68, leap: 0, weave: 0.14, delay: 9.0 },
        // Escorts, so a full rail is not six people watching one buck. One per
        // extra hunter, spaced through the wave.
        //
        // Deliberately kept back in the depth band. `npm run check:herd`
        // measures how far a deer travels during a full lag-compensation
        // rewind, in body lengths — and a FAST animal in the SHALLOW band is
        // the worst case for that, because shallow means large on screen and
        // fast means it has moved. Escorts at z ≈ 0.2 running 0.88 pushed the
        // worst case over the design bar on their own. Deeper, they are just
        // as hard to hit and they no longer make everyone else's view of a
        // laggy player's kill look wrong.
        ...Array.from({ length: extra }, (_, i) => ({
          kind: 'buck' as const,
          rack: [8, 10, 12][i % 3]!,
          speed: 0.66 + (i % 3) * 0.06,
          zMin: 0.4 + (i % 4) * 0.1,
          zMax: 0.56 + (i % 4) * 0.1,
          leap: i % 2 ? 0.12 : 0,
          weave: 0.16,
          delay: 3.2 + i * 1.7,
        })),
      ],
      varmints: [],
    };
  }

  // A hunt wave. Deer count, speed, depth band and doe density all ramp.
  const count = 3 + Math.round(tier * 3) + rng.int(0, 1) + extra;
  const recipes: DeerRecipe[] = [];
  const doeChance = index === 1 ? 0 : 0.18 + tier * 0.22;
  const duration = 13_000 + Math.round(tier * 4000);

  /**
   * The doe budget. Two bounds, both learned from `npm run check:herd`:
   *
   *   · A CEILING of 40% of the herd. An unlucky roll used to produce a wave
   *     with more does than bucks, which is not "hard", it is a wave where the
   *     correct play is to stop shooting.
   *   · A FLOOR of one from wave 2 onward. Left purely to chance, a seed could
   *     — and in testing did — run five waves without a single doe. The doe
   *     rule is the spine of the game; a player must not reach wave 6 having
   *     never met it.
   */
  const maxDoes = Math.floor(count * 0.4);
  let does = 0;
  const kinds: DeerKind[] = [];
  for (let i = 0; i < count; i++) {
    const isDoe = does < maxDoes && rng.chance(doeChance);
    if (isDoe) does++;
    kinds.push(isDoe ? 'doe' : 'buck');
  }
  if (index >= 2 && does === 0) {
    // Never the first animal of the wave: the opening shot should be a clean
    // one, and an opener that punishes you reads as unfair rather than tense.
    kinds[rng.int(1, count - 1)] = 'doe';
  }

  for (let i = 0; i < count; i++) {
    const isDoe = kinds[i] === 'doe';
    // Spread arrivals across the wave with jitter, so the herd is a stream and
    // not a volley. The last third is left clear so nothing spawns that cannot
    // finish crossing before the wave ends.
    const delay = (i / count) * (duration / 1000) * 0.72 + rng.range(0, 0.6);
    recipes.push({
      kind: isDoe ? 'doe' : 'buck',
      rack: isDoe ? 0 : rackForTier(rng, tier),
      speed: (isDoe ? 0.66 : 0.5) + tier * 0.42 + rng.range(-0.06, 0.12),
      // Later sites push the herd deeper — a smaller target at the same speed.
      zMin: 0.18 + tier * 0.3,
      zMax: 0.46 + tier * 0.42,
      leap: rng.chance(0.25 + tier * 0.3) ? rng.range(0.1, 0.22) : 0,
      weave: rng.chance(0.3 + tier * 0.4) ? rng.range(0.1, 0.3) : 0,
      delay,
    });
  }
  return { index, kind, site, durationMs: duration, shells: 6, recipes, varmints: [] };
}

function rackForTier(rng: import('./rng.js').Rng, tier: number): number {
  const roll = rng.next() + tier * 0.42;
  if (roll > 1.24) return 14;
  if (roll > 1.0) return 12;
  if (roll > 0.72) return 10;
  if (roll > 0.4) return 8;
  return 6;
}

/**
 * The varmint round. Comic relief between hunt waves, and the only place the
 * game asks for speed rather than patience: things pop up, you clear them, and
 * the skunk is there to punish a player who has stopped looking.
 */
function planVarmints(index: number, rng: import('./rng.js').Rng, extra: number): VarmintRecipe[] {
  const out: VarmintRecipe[] = [];
  const total = (index === 4 ? 16 : 22) + extra * 4;
  const skunks = (index === 4 ? 3 : 5) + Math.floor(extra / 2);
  const upTime = index === 4 ? 1.5 : 1.15;

  for (let i = 0; i < total; i++) {
    out.push({
      kind: i < skunks ? 'skunk' : 'critter',
      sx: rng.range(-0.82, 0.82),
      sy: rng.range(0.02, 0.62),
      delay: 0.8 + (i / total) * 12.4 + rng.range(-0.25, 0.25),
      up: upTime * rng.range(0.85, 1.15),
    });
  }
  // Shuffled so the skunks are not all in the first second. Fisher-Yates on the
  // shared RNG, so every client agrees without being told the order.
  for (let i = out.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    const tmp = out[i]!.kind;
    out[i]!.kind = out[j]!.kind;
    out[j]!.kind = tmp;
  }
  return out;
}

export const VARMINT_POINTS = 250;
export const SKUNK_PENALTY = 400;
/** Radius of a varmint's hitbox, in stage units. */
export const VARMINT_R = 0.085;
